import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { DropdownItem } from "src/design-system/Dropdown";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { CompareModelsModal } from "src/genai/ModelSelector/CompareModelsModal";
import { selectableModels } from "src/genai/ModelSelector/facets";
import { ModelSelectorDropdown } from "src/genai/ModelSelector/ModelSelectorDropdown";
import {
  ModelOption,
  ProviderFilter,
  RegionFilter,
} from "src/genai/ModelSelector/types";
import {
  resolveNudge,
  UpgradeNudge,
} from "src/genai/ModelSelector/UpgradeNudge";

export type ModelPickerProps = {
  /** Models with their rich comparison metadata. */
  models: ModelOption[];
  selectedModelId: string | undefined;
  onSelect: (modelId: string) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
  /** Show the lifecycle nudge when the selection is superseded/deprecated/retired. */
  showUpgradeNudge?: boolean;
  /**
   * Friendly label shown when the selection matches no model (removed model /
   * deleted BYOK credential). Falls back to the raw id.
   */
  missingModelDisplayName?: string;
  /** Initial residency filter applied in the Compare modal. */
  defaultRegion?: RegionFilter;
  /** Initial provider filter applied in the Compare modal. */
  defaultProvider?: ProviderFilter;
  /** data-loc prefix for testing. */
  dataLoc?: string;
  /**
   * The current selection can't be resolved to an option yet because the model
   * list is still loading. Shows a neutral skeleton in the trigger instead of
   * an "Unavailable" pill until the list settles.
   */
  isResolvingSelection?: boolean;
};

/**
 * LLM model picker: a simple sectioned dropdown (model rows + residency badges)
 * with a pinned "Compare models" footer that opens a rich comparison modal, plus
 * a nudge when the selection is superseded or deprecated.
 */
export const ModelPicker: React.FC<ModelPickerProps> = ({
  models,
  selectedModelId,
  onSelect,
  disabled = false,
  isReadOnly = false,
  showUpgradeNudge = true,
  missingModelDisplayName,
  defaultRegion = "any",
  defaultProvider = "any",
  dataLoc = "model-picker",
  isResolvingSelection = false,
}) => {
  const { isOpen, isVisible, openModal, closeModal, afterLeave } = useModal();
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  // Reset the dismissal whenever the selection changes
  const [lastSelectedModelId, setLastSelectedModelId] =
    useState(selectedModelId);
  if (selectedModelId !== lastSelectedModelId) {
    setLastSelectedModelId(selectedModelId);
    setNudgeDismissed(false);
  }

  // Deprecated models are not offered for selection, but the nudge resolves
  // against the full list, so it can still name one that the dropdown hides.
  const offeredModels = selectableModels(models, selectedModelId);
  const nudge = resolveNudge({
    models,
    offeredModels,
    selected: models.find((model) => model.id === selectedModelId),
    isReadOnly,
  });
  const switchTarget = nudge?.switchTarget;

  const selectModel = (modelId: string) => {
    onSelect(modelId);
    setNudgeDismissed(false);
  };

  return (
    <div className="w-full" data-loc={`${dataLoc}-root`}>
      <ModelSelectorDropdown
        dataLoc={dataLoc}
        disabled={disabled || isReadOnly}
        footer={
          // "Compare models" is an action, not a model choice, so it reuses the
          // shared DropdownItem row (grey, hover-highlighted) with a plus icon,
          // rather than a link-styled button. -mx-4 cancels the footer's
          // horizontal padding so the row is full-bleed and aligns with the
          // option rows above; -mb-2 cancels the panel's bottom padding.
          <div className="-mx-4 -mb-2">
            <DropdownItem
              data-loc={`${dataLoc}-compare-link`}
              role="button"
              tabIndex={0}
              onClick={() => openModal()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openModal();
                }
              }}
            >
              <span className="flex items-center gap-x-2">
                <Icon icon={faPlus} size="sm" />
                Compare models
              </span>
            </DropdownItem>
          </div>
        }
        isResolvingSelection={isResolvingSelection}
        missingModelDisplayName={missingModelDisplayName}
        modelOptions={offeredModels}
        selectedModelId={selectedModelId}
        onSelect={selectModel}
      />

      {showUpgradeNudge && nudge && !nudgeDismissed && (
        <UpgradeNudge
          action={
            switchTarget
              ? {
                  text: "Switch model",
                  onClick: () => selectModel(switchTarget.id),
                }
              : { text: "Dismiss", onClick: () => setNudgeDismissed(true) }
          }
          nudge={nudge}
        />
      )}

      {isVisible && (
        <CompareModelsModal
          afterLeave={afterLeave}
          dataLoc={`${dataLoc}-compare`}
          defaultProvider={defaultProvider}
          defaultRegion={defaultRegion}
          isOpen={isOpen}
          isReadOnly={isReadOnly}
          models={offeredModels}
          selectedModelId={selectedModelId}
          onClose={closeModal}
          onSelect={selectModel}
        />
      )}
    </div>
  );
};
